// # 화면 언어 — 한국어 원문이 열쇠다
//
// 슬랙은 언어를 사람마다 고르게 한다(215058658). 우리는 서버의 보기 설정
// (`/v1/me/appearance` 의 `locale`)으로 받아 `set_locale` 로 꽂는다.
//
// ## 왜 원문이 열쇠인가
// `t('settings.title')` 같은 열쇠를 따로 두면 화면 코드를 읽는 사람이 무슨 글자가 뜨는지
// 모르고, 한국어 문장을 고칠 때마다 열쇠 표와 두 번 맞춰야 한다. 원문을 그대로 열쇠로 두면
// 코드는 지금처럼 읽히고, 번역이 없는 문장은 **원문 그대로** 뜬다 — 빈칸이 뜨지 않는다.
//
// ## 빠진 번역은 시험이 잡는다
// `i18n.test.ts` 가 `packages/ui`·`packages/app` 원문에서 `t('…')` 를 주워 영어 표에 모두
// 있는지, 표에 쓰이지 않는 줄은 없는지 본다. 표만 고치고 화면을 안 고치거나 그 반대면 깨진다.
//
// ## 복수형과 조사는 원문 안의 자리로
// `{n|reply|replies}` 는 `vars.n` 의 수로 형태를 고른다(`Intl.PluralRules`). 한국어 원문은 복수형이
// 없으니 번역 쪽에만 쓴다. `{name:와/과}` 는 이름 끝의 받침으로 조사를 붙인다(`josa`) — 한국어
// 원문에서 `와(과)` 같은 겸용 표기를 쓰지 않으려고 둔다. 수로 넘긴 자리는 그 언어의 자릿수 묶음으로
// 쓴다(`1,234`) — 번호처럼 묶으면 안 되는 값은 글자로 넘긴다.
//
// ## 뜻이 다르면 원문도 다르다 — 맥락 꼬리 `〔…〕`
// 한국어로는 같은 낱말이 영어로는 둘일 때가 있다(사이드바 '스레드' = Threads, 판 하나의 '스레드' = Thread).
// 원문 끝에 `〔판〕` 처럼 맥락을 달면 열쇠가 갈리고, 한국어 화면에는 꼬리를 떼고 보인다(3차 F18).
// 문장을 조금 바꿔 나눌 수 있으면 그쪽이 먼저다('더 보기' ↔ '더 불러오기'). 꼬리는 낱말 하나가 둘로 갈릴 때만.
//
// ## 일본어는 표만 있다
// `ja` 는 `JA → EN → 원문` 순서로 떨어진다. 고르기(`LOCALES`)에는 표가 찰 때까지 내지 않는다.
//
// ## 표는 고를 때 받는다
// 영어 표(`i18n_en.ts`)는 `set_locale('en')` 이 처음 불릴 때 따로 받는다. 받는 동안은 원문이 뜨고,
// 다 받으면 다시 그린다. 한국어만 쓰는 사람은 표를 한 번도 받지 않는다. 받기를 기다려야 하는
// 쪽(시험·미리보기 도구)은 `locale_ready()` 를 기다린다.
//
// ## 아직 전부가 아니다
// 번역은 틀(설정·보기·계정·사이드바 도구·작성칸)부터다. 나머지 화면은 `t` 로 감싸는 만큼
// 늘어난다 — 감싸지 않은 문장은 어느 언어에서든 한국어로 뜬다.

import { useSyncExternalStore } from 'react'

export type Locale = 'ko' | 'en' | 'ja'

/** 고르기에 내는 언어. 서버의 보기 설정도 이 둘만 받는다 — `ja` 는 표가 찬 뒤에 더한다. */
export type PickableLocale = Exclude<Locale, 'ja'>

export const LOCALES: readonly { key: PickableLocale; label: string }[] = [
  { key: 'ko', label: '한국어' },
  { key: 'en', label: 'English' },
]

/** 한국어 원문 → 일본어. 비어 있으면 영어, 그다음 원문으로 떨어진다. */
export const JA: Readonly<Record<string, string>> = {}

/**
 * 표나 배열에 적어 둔 원문 표시. 그대로 돌려줄 뿐이다 — 시험이 이 표시로 원문을 줍는다.
 * 그리는 자리에서 `t(label)` 로 번역한다.
 */
export function msg(ko: string): string {
  return ko
}

let current: Locale = 'ko'
const listeners = new Set<() => void>()
let en: Readonly<Record<string, string>> | null = null
let loading: Promise<void> = Promise.resolve()

function tell(): void {
  for (const l of listeners) l()
}

/** 지금 언어를 바꾼다. 구독한 화면이 다시 그려지고, 표가 없으면 받은 뒤에 한 번 더 그려진다. */
export function set_locale(next: Locale): void {
  if (next === current) return
  current = next
  tell()
  if (next !== 'ko' && en === null) {
    loading = import('./i18n_en.ts').then(
      (m) => {
        en = m.EN
        tell()
      },
      // 표를 못 받으면 원문으로 남는다 — 빈칸보다 한국어가 낫다.
      (e: unknown) => console.error('번역 표를 받지 못했다', e),
    )
  }
}

/** 고른 언어의 표를 다 받았을 때. 한국어거나 이미 받았으면 곧바로다. */
export function locale_ready(): Promise<void> {
  return loading
}

export function locale_now(): Locale {
  return current
}

function subscribe(listen: () => void): () => void {
  listeners.add(listen)
  return () => listeners.delete(listen)
}

/** `Intl` 에 넘기는 언어 태그. */
export function bcp47(locale: Locale): string {
  return locale === 'ko' ? 'ko-KR' : locale === 'ja' ? 'ja-JP' : 'en-US'
}

/** 수에 맞는 형태. 한국어·일본어는 늘 `other` 다. */
export function plural(
  n: number,
  forms: { readonly one: string; readonly other: string },
  locale: Locale = current,
): string {
  // 만들기가 비싸다(줄마다 부르면 목록 한 번 그리는 데 수백 번이다). 언어마다 하나를 둔다.
  PLURALS[locale] ??= new Intl.PluralRules(bcp47(locale))
  return PLURALS[locale].select(n) === 'one' ? forms.one : forms.other
}

const PLURALS: Partial<Record<Locale, Intl.PluralRules>> = {}

// 받침 있는 쪽이 앞이다.
const PAIRS: readonly (readonly [string, string])[] = [
  ['은', '는'],
  ['이', '가'],
  ['을', '를'],
  ['과', '와'],
  ['으로', '로'],
  ['아', '야'],
  ['이에요', '예요'],
  ['이랑', '랑'],
  ['이나', '나'],
]

type Tail = '' | 'ㄹ' | '+'

// 숫자를 읽은 소리의 받침: 영(ㅇ)·일(ㄹ)·이·삼(ㅁ)·사·오·육(ㄱ)·칠(ㄹ)·팔(ㄹ)·구.
const DIGIT_TAIL: readonly Tail[] = ['+', 'ㄹ', '', '+', '', '', '+', 'ㄹ', 'ㄹ', '']

function hangul_tail(code: number): Tail {
  const jong = (code - 0xac00) % 28
  return jong === 0 ? '' : jong === 8 ? 'ㄹ' : '+'
}

/** 끝소리의 받침. 없으면 `''`, ㄹ 이면 `'ㄹ'`, 그 밖은 `'+'`. 로마자는 읽는 소리를 어림한다. */
function tail(word: string): Tail {
  const w = word.replace(/[\s'"`)\]}>.,!?…]+$/u, '').toLowerCase()
  const last = w.at(-1) ?? ''
  const code = last.charCodeAt(0)
  if (code >= 0xac00 && code <= 0xd7a3) return hangul_tail(code)
  if (/[0-9]/.test(last)) return DIGIT_TAIL[Number(last)] ?? ''
  if (last === 'l') return 'ㄹ'
  return /(?:ng|[bckmnpt])$/.test(w) ? '+' : ''
}

/**
 * 이름에 조사를 붙인다. `josa('디자인', '와/과')` → `디자인과`. 쌍의 순서는 상관없다.
 * `으로/로` 는 ㄹ 받침에서 `로` 다. 모르는 쌍이면 앞의 것을 그대로 붙인다.
 */
export function josa(word: string, pair: string): string {
  const [a = '', b = ''] = pair.split('/')
  const found = PAIRS.find(([x, y]) => (x === a && y === b) || (x === b && y === a))
  if (found === undefined) return word + a
  const [with_tail, without] = found
  const t = tail(word)
  if (with_tail === '으로') return word + (t === '+' ? with_tail : without)
  return word + (t === '' ? without : with_tail)
}

type Vars = Readonly<Record<string, string | number>>

const numbers = new Map<Locale, Intl.NumberFormat>()

function number_text(n: number, locale: Locale): string {
  let f = numbers.get(locale)
  if (f === undefined) {
    f = new Intl.NumberFormat(bcp47(locale))
    numbers.set(locale, f)
  }
  return f.format(n)
}

/** 순수 번역. 표에 없으면 원문 그대로 — 빈칸보다 한국어가 낫다. */
export function translate(locale: Locale, ko: string, vars?: Vars): string {
  const bare = ko.endsWith('〕') ? ko.replace(/〔[^〕]*〕$/, '') : ko
  const base =
    locale === 'en' ? (en?.[ko] ?? bare) : locale === 'ja' ? (JA[ko] ?? en?.[ko] ?? bare) : bare
  if (vars === undefined) return base
  return base.replace(
    /\{(\w+)(?:([|:])([^}]*))?\}/g,
    (whole, name: string, mark?: string, rest?: string) => {
      const v = vars[name]
      if (v === undefined) return whole
      if (mark === '|') {
        const [one = '', other = one] = (rest ?? '').split('|')
        return plural(Number(v), { one, other }, locale)
      }
      const text = typeof v === 'number' ? number_text(v, locale) : v
      if (mark === ':') return josa(text, rest ?? '')
      return text
    },
  )
}

/** 화면에서 쓰는 번역 함수. 언어가 바뀌면 이 훅을 쓴 컴포넌트가 다시 그려진다. */
/**
 * 화면이 구독하는 값 — 언어 **와** 표를 받았는가. 언어만 보면 영어를 고른 순간(표 없음, 원문)과
 * 표가 도착한 순간이 같은 값이라 두 번째 알림에 아무도 다시 그리지 않고, 화면이 한국어 반 영어
 * 반으로 굳는다(3차 F46). 반환값이 바뀌어야 `t` 가 새 함수가 되어 컴파일러 메모도 풀린다.
 */
export function locale_edition(): string {
  return current === 'ko' || en !== null ? current : `${current}…`
}

export function useT(): (ko: string, vars?: Vars) => string {
  const seen = useSyncExternalStore(subscribe, locale_edition, locale_edition)
  // 함수는 `seen` 을 잡는다 — 떼어 낸 언어(`en`)만 잡으면 `en…` 과 `en` 이 같은 열쇠라 컴파일러가 표를
  // 받기 전의 `t` 를 그대로 돌려주고, `t` 에만 기대는 제목·자리표시자가 한국어로 굳는다(3차 F106).
  return (ko, vars) => translate(locale_of(seen), ko, vars)
}

/**
 * 지금 언어. `Intl` 로 그리는 값(시각·날짜)은 `t` 를 안 거쳐 컴파일러가 언어와 무관한 값으로 묵혀 둔다 —
 * 새로 고친 뒤 조직의 언어가 늦게 오면 메시지 시각만 "오전 10:29" 로 남았다(3차 F265). 그런 자리는 이걸 넘긴다.
 */
export function useLocale(): Locale {
  return locale_of(useSyncExternalStore(subscribe, locale_edition, locale_edition))
}

function locale_of(edition: string): Locale {
  return (edition.endsWith('…') ? edition.slice(0, -1) : edition) as Locale
}
